'use client';

import { getAuthToken } from '@/utils/profile/authSession';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { mutate } from 'swr';
import {
    Check,
    Clock,
    Gift,
    MessageSquare,
    PhoneCall,
    Phone,
    User as UserIcon,
    Mail,
} from 'lucide-react';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import { useToast } from '@/hooks/use-toast';
import { notifyLandingRegister } from '@/utils/profile/landing-register/notifyLandingRegister';
import { updateUser } from '@/utils/profile/updateUser';
import { LayoutProps } from '@/types/generalSettings';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import LoaderSpin from '@/components/LoaderSpin';

export type PlatformType = 'broker' | 'exchange' | 'props' | 'prop' | 'gold';

export interface PlatformBonusCardProps {
    platformName?: string;
    platformEnName?: string;
    logoUrl?: string;
    platformType?: PlatformType;
    bonusImages?: LayoutProps.BonusPlatforms | LayoutProps.BonusImageItem;
}

const getPersianDate = (): string => {
    try {
        const now = new Date();
        const month = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { month: 'long' }).format(now);
        const year = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric' }).format(now);
        return `${month} ${year}`;
    } catch {
        return '';
    }
};

const toPersianDigits = (n: number | string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return String(n).replace(/\d/g, (d) => persianDigits[Number(d)]);
};

const normalizeToEnglishDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return str
        .split('')
        .map((ch) => {
            const pIdx = persianDigits.indexOf(ch);
            if (pIdx > -1) return String(pIdx);
            const aIdx = arabicDigits.indexOf(ch);
            if (aIdx > -1) return String(aIdx);
            return ch;
        })
        .join('');
};

/**
 * Isolated Countdown Timer Component
 * Re-renders only itself every 1 second without causing re-renders in parent card or form
 */
const BonusCountdownTimer: React.FC = () => {
    const [timeLeft, setTimeLeft] = useState<{ hours: string; minutes: string; seconds: string }>({
        hours: '۰۰',
        minutes: '۰۰',
        seconds: '۰۰',
    });

    useEffect(() => {
        const calculateRemaining = () => {
            const now = new Date();
            const endOfDay = new Date(now);
            endOfDay.setHours(23, 59, 59, 999);
            let diff = endOfDay.getTime() - now.getTime();
            if (diff < 0) diff = 0;

            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);

            setTimeLeft({
                hours: toPersianDigits(String(h).padStart(2, '0')),
                minutes: toPersianDigits(String(m).padStart(2, '0')),
                seconds: toPersianDigits(String(s).padStart(2, '0')),
            });
        };

        calculateRemaining();
        const interval = setInterval(calculateRemaining, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className='flex items-center justify-between gap-2 px-3 py-2 sm:px-3 sm:py-2 rounded-2xl bg-gradient-to-r from-blue-50/70 via-white/90 to-indigo-50/70 dark:from-white/[0.01] dark:via-white/[0.01] dark:to-white/[0.01] border border-blue-200/70 dark:border-white/10 mb-4 shadow-xs backdrop-blur-sm'>
            {/* Right: Icon & Title with active deal dot */}
            <div className='flex items-center gap-2'>
                <div className='relative flex items-center justify-center size-7 rounded-xl bg-blue-500/10 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400 shrink-0'>
                    <Clock className='size-3.5' />
                </div>
                <div className='flex flex-col'>
                    <span className='text-xs font-bold text-gray-800 dark:text-gray-200 leading-tight'>
                        پایان آفر
                    </span>
                </div>
            </div>

            {/* Left: Discrete Digit Boxes */}
            <div className='flex items-center gap-1 sm:gap-1.5' dir='ltr'>
                {/* Hours Card */}
                <div className='flex flex-col items-center justify-center bg-white dark:bg-white/10 border border-gray-200/80 dark:border-white/10 rounded-lg px-2 py-0.5 min-w-[34px] shadow-2xs'>
                    <span className='text-xs sm:text-sm font-black text-gray-900 dark:text-white leading-tight'>
                        {timeLeft.hours}
                    </span>
                    <span className='text-[8px] text-gray-400 dark:text-gray-400 leading-none'>
                        ساعت
                    </span>
                </div>

                <span className='font-bold text-gray-400 dark:text-gray-500 animate-pulse text-xs'>
                    :
                </span>

                {/* Minutes Card */}
                <div className='flex flex-col items-center justify-center bg-white dark:bg-white/10 border border-gray-200/80 dark:border-white/10 rounded-lg px-2 py-0.5 min-w-[34px] shadow-2xs'>
                    <span className='text-xs sm:text-sm font-black text-gray-900 dark:text-white leading-tight'>
                        {timeLeft.minutes}
                    </span>
                    <span className='text-[8px] text-gray-400 dark:text-gray-400 leading-none'>
                        دقیقه
                    </span>
                </div>

                <span className='font-bold text-blue-500 dark:text-blue-400 animate-pulse text-xs'>
                    :
                </span>

                {/* Seconds Card (Active Accent) */}
                <div className='flex flex-col items-center justify-center bg-blue-50 dark:bg-blue-500/30 border border-blue-200/80 dark:border-blue-400/25 rounded-lg px-2 py-0.5 min-w-[34px] shadow-2xs'>
                    <span className='text-xs sm:text-sm font-black text-blue-600 dark:text-blue-200 leading-tight'>
                        {timeLeft.seconds}
                    </span>
                    <span className='text-[8px] text-blue-500/80 dark:text-blue-200 leading-none'>
                        ثانیه
                    </span>
                </div>
            </div>
        </div>
    );
};

const PlatformBonusCard: React.FC<PlatformBonusCardProps> = ({
    platformName = 'بروکر',
    platformEnName = '',
    logoUrl = '',
    platformType = 'broker',
    bonusImages,
}) => {
    const { toast } = useToast();
    const { isLoggedInGlobal, user, mobile } = useVerifyUser();

    // Steps: 'cta' | 'logged_in_confirm' | 'login_form' | 'update_profile' | 'done'
    const [step, setStep] = useState<
        'cta' | 'logged_in_confirm' | 'login_form' | 'update_profile' | 'done'
    >('cta');
    const [loading, setLoading] = useState(false);
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');

    const activeMobile = user?.mobile || mobile || '';

    // Initialize user details when available
    useEffect(() => {
        if (user) {
            if (user.firstName) setFullName(user.firstName);
            if (user.email) setEmail(user.email);
        }
    }, [user]);

    const getReferrerUrl = useCallback((): string => {
        if (typeof window === 'undefined') return '';
        try {
            const { host, pathname, search } = window.location;
            let cleanSearch = search || '';
            if (cleanSearch) {
                if (!cleanSearch.includes('utm_source=')) {
                    cleanSearch = `${cleanSearch}&utm_source=bonus`;
                }
            } else {
                cleanSearch = '?utm_source=bonus';
            }
            return `${host}${pathname}${cleanSearch}`;
        } catch {
            return '';
        }
    }, []);

    const [referrerUrl, setReferrerUrl] = useState<string>('');

    useEffect(() => {
        setReferrerUrl(getReferrerUrl());
    }, [getReferrerUrl]);

    // Handle initial CTA button click
    const handleCtaClick = () => {
        if (isLoggedInGlobal && activeMobile) {
            setStep('logged_in_confirm');
        } else {
            setStep('login_form');
        }
    };

    // Logged in user registers with their existing mobile number
    const handleLoggedInRegister = async () => {
        setLoading(true);
        try {
            const currentToken = getAuthToken();
            const rawPhone = normalizeToEnglishDigits(activeMobile);
            const currentSourceUrl = getReferrerUrl() || referrerUrl;

            if (!currentToken) {
                toast({
                    className: 'bg-red-600 text-white rounded-2xl',
                    title: 'نشست کاربری شما منقضی شده است. لطفاً دوباره وارد شوید.',
                });
                setStep('login_form');
                return;
            }

            if (!rawPhone) {
                setStep('login_form');
                return;
            }

            await notifyLandingRegister(rawPhone, currentToken, 'bonus', currentSourceUrl);

            toast({
                className: 'bg-emerald-600 text-white rounded-2xl',
                title: 'شماره شما ثبت شد. لطفاً اطلاعات خود را بررسی نمایید.',
                duration: 3500,
            });
            // Move to name & email update step
            setStep('update_profile');
        } catch (err) {
            console.error('Registration failed:', err);
            toast({
                className: 'bg-red-600 text-white rounded-2xl',
                title: 'خطا در ثبت شماره. لطفاً مجدداً تلاش کنید.',
            });
            setStep('login_form');
        } finally {
            setLoading(false);
        }
    };

    // Submit profile update (Full Name & Email)
    const handleProfileUpdateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (fullName.trim()) {
                await updateUser({ fullName: fullName.trim(), email: email.trim() });
                mutate('verifyUser');
            }
            toast({
                className: 'bg-emerald-600 text-white rounded-2xl',
                title: 'اطلاعات شما با موفقیت تکمیل شد!',
                duration: 4000,
            });
            setStep('done');
        } catch {
            // Even if update has non-blocking error, user is already registered for bonus
            setStep('done');
        } finally {
            setLoading(false);
        }
    };

    const persianDate = useMemo(() => getPersianDate(), []);

    const titlePrefix = useMemo(() => {
        const trimmedName = (platformName || '').trim();
        switch (platformType) {
            case 'props':
            case 'prop': {
                const cleanName = trimmedName
                    .replace(/^پراپ\s+فرم\s+/, '')
                    .replace(/^پراپ\s+/, '')
                    .trim();
                return `کد تخفیف پراپ فرم ${cleanName || trimmedName}`;
            }
            case 'gold': {
                const cleanName = trimmedName
                    .replace(/^(پلتفرم\s+طلا|سایت\s+طلا|پلتفرم|سایت)\s+/, '')
                    .trim();
                return `کد تخفیف و کد هدیه طلا رایگان ${cleanName || trimmedName}`;
            }
            case 'exchange': {
                const cleanName = trimmedName.replace(/^صرافی\s+/, '').trim();
                return `بونوس صرافی ${cleanName || trimmedName}`;
            }
            case 'broker':
            default: {
                const cleanName = trimmedName.replace(/^بروکر\s+/, '').trim();
                return `بونوس بروکر ${cleanName || trimmedName}`;
            }
        }
    }, [platformName, platformType]);

    const subtitleText = useMemo(() => {
        const trimmedName = (platformName || '').trim();
        switch (platformType) {
            case 'props':
            case 'prop': {
                const cleanName = trimmedName
                    .replace(/^پراپ\s+فرم\s+/, '')
                    .replace(/^پراپ\s+/, '')
                    .trim();
                return `فرم دریافت کد تخفیف اختصاصی و چالش‌های پراپ فرم ${cleanName || trimmedName}`;
            }
            case 'gold': {
                const cleanName = trimmedName
                    .replace(/^(پلتفرم\s+طلا|سایت\s+طلا|پلتفرم|سایت)\s+/, '')
                    .trim();
                return `فرم دریافت کد هدیه و تخفیف طلای رایگان ${cleanName || trimmedName}`;
            }
            case 'exchange': {
                const cleanName = trimmedName.replace(/^صرافی\s+/, '').trim();
                return `فرم دریافت آخرین بونوس‌ها و آفرهای ویژه صرافی ${cleanName || trimmedName}`;
            }
            case 'broker':
            default: {
                const cleanName = trimmedName.replace(/^بروکر\s+/, '').trim();
                return `فرم دریافت آخرین بونوس‌ها و آفرهای ویژه بروکر ${cleanName || trimmedName}`;
            }
        }
    }, [platformName, platformType]);

    const offerTypeWord = useMemo(() => {
        if (platformType === 'gold') return 'هدیه و تخفیف';
        if (platformType === 'props' || platformType === 'prop') return 'کد تخفیف';
        return 'بونوس';
    }, [platformType]);

    const resolvedBonusImg = useMemo(() => {
        if (!bonusImages) return undefined;
        if ('desktop' in bonusImages || 'mobile' in bonusImages) {
            return bonusImages as LayoutProps.BonusImageItem;
        }
        const platforms = bonusImages as LayoutProps.BonusPlatforms;
        const key = (
            platformType === 'prop' ? 'props' : platformType
        ) as keyof LayoutProps.BonusPlatforms;
        return platforms[key];
    }, [bonusImages, platformType]);

    const desktopBonusImg = resolvedBonusImg?.desktop?.sourceUrl || '/images/bonus/bonus-perks.png';
    const mobileBonusImg =
        resolvedBonusImg?.mobile?.sourceUrl ||
        resolvedBonusImg?.desktop?.sourceUrl ||
        '/images/bonus/bonus-perks.png';
    const imageTitle = `${titlePrefix} ${persianDate}`.trim();
    return (
        <section
            aria-label={imageTitle}
            className='w-full xl:w-[97%] mx-auto my-8 relative rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white/90 dark:bg-white/5 backdrop-blur-md overflow-hidden shadow-sm transition-all duration-300'
        >
            {/* Ambient Blurred Platform Logo Background */}
            {logoUrl && (
                <div
                    aria-hidden='true'
                    className='absolute inset-0 pointer-events-none select-none overflow-hidden z-0'
                >
                    {/* Layer 1: Soft ambient gradient from the logo */}
                    <div className='absolute -inset-10 flex items-center justify-center opacity-20 filter blur-3xl scale-125 transform transition-all duration-700'>
                        <Image
                            src={logoUrl}
                            alt=''
                            width={300}
                            height={300}
                            className='object-cover w-full h-full'
                            priority={false}
                        />
                    </div>
                    {/* Layer 2: Subtle inner glow to maintain platform color identity */}
                    <div className='absolute inset-0 flex items-center justify-center opacity-20 filter blur-xl scale-95'>
                        <Image
                            src={logoUrl}
                            alt=''
                            width={220}
                            height={220}
                            className='object-contain w-3/4 h-3/4 max-w-sm'
                            priority={false}
                        />
                    </div>
                </div>
            )}

            {/* Inner Content Grid */}
            <div className='relative z-10 p-5 sm:p-7 lg:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start'>
                {/* Right Side (RTL): Platform Info & Seamless Bonus Perks List */}
                <div className='md:col-span-6 xl:col-span-7 lg:pt-3 flex flex-col justify-between gap-4'>
                    <div>
                        {/* Platform Header Row */}
                        <div className='flex items-center gap-3 mb-3'>
                            {logoUrl && (
                                <div className='w-11 h-11 rounded-xl bg-white dark:bg-white/10 border border-gray-200 dark:border-white/15 p-1 flex items-center justify-center shadow-xs shrink-0 overflow-hidden'>
                                    <Image
                                        src={logoUrl}
                                        alt={`لوگوی ${platformName}`}
                                        title={platformName}
                                        width={40}
                                        height={40}
                                        className='object-contain w-full h-full rounded-lg'
                                    />
                                </div>
                            )}
                            <div className='min-w-0 flex-1'>
                                <span className='text-xs font-semibold text-primary/70 dark:text-white/60 block truncate'>
                                    {platformEnName || platformName}
                                </span>
                                <h3 className='text-base sm:text-lg lg:text-xl font-black text-gray-900 dark:text-white tracking-tight leading-snug break-words'>
                                    <span>{titlePrefix}</span>{' '}
                                    <span className='inline-block text-blue-600 dark:text-blue-400 font-extrabold mr-1 whitespace-nowrap'>
                                        {persianDate}
                                    </span>
                                </h3>
                            </div>
                        </div>

                        {/* Dynamic Subtitle */}
                        <p className='text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300 font-medium mb-4 leading-relaxed'>
                            {subtitleText}
                        </p>
                    </div>

                    {/* Seamless Bonus Perks Image (Right-aligned, native clean resolution with dark mode filter) */}
                    <div className='w-full max-w-xl flex items-center justify-start'>
                        {/* Desktop Image */}
                        <div className='hidden sm:flex items-center justify-start w-full max-w-[562px]'>
                            <Image
                                src={desktopBonusImg}
                                alt={`هدایا و آفرهای ویژه ${platformName}`}
                                title={imageTitle}
                                width={562}
                                height={106}
                                className='w-full select-none h-auto object-contain object-right dark:invert dark:hue-rotate-180 dark:brightness-110 transition-all duration-300'
                            />
                        </div>
                        {/* Mobile Image */}
                        <div className='flex sm:hidden items-center justify-start w-full max-w-[264px]'>
                            <Image
                                src={mobileBonusImg}
                                alt={`هدایا و آفرهای ویژه ${platformName}`}
                                title={imageTitle}
                                width={264}
                                height={172}
                                className='w-full select-none h-auto object-contain object-right dark:invert dark:hue-rotate-180 dark:brightness-110 transition-all duration-300'
                            />
                        </div>
                    </div>
                </div>

                {/* Left Side (RTL): Interactive Gift Box & Form Wizard */}
                <div className='md:col-span-6 xl:col-span-5 flex flex-col justify-center bg-gradient-to-b from-blue-50/70 to-indigo-50/30 dark:from-white/[0.04] dark:to-transparent border border-blue-100/80 dark:border-white/10 rounded-2xl p-5 sm:p-6 relative overflow-hidden'>
                    {/* Animated Gift Box & Cycling Bonus Amounts */}
                    <div className='flex items-center justify-center gap-3 mb-4'>
                        <div className='gift-dance-box w-16 h-16 flex items-center justify-center select-none'>
                            <span
                                className='text-5xl leading-none filter drop-shadow-md'
                                role='img'
                                aria-label='هدیه'
                            >
                                🎁
                            </span>
                        </div>

                        <div
                            dir='ltr'
                            className='relative w-28 h-12 flex items-center justify-start text-3xl sm:text-4xl font-black text-blue-600 dark:text-blue-400 font-mono select-none'
                        >
                            <span
                                className='amt-cycle absolute inset-0 flex items-center'
                                style={{ animationDelay: '0s' }}
                            >
                                $50
                            </span>
                            <span
                                className='amt-cycle absolute inset-0 flex items-center'
                                style={{ animationDelay: '1.25s' }}
                            >
                                $100
                            </span>
                            <span
                                className='amt-cycle absolute inset-0 flex items-center'
                                style={{ animationDelay: '2.5s' }}
                            >
                                $200
                            </span>
                            <span
                                className='amt-cycle absolute inset-0 flex items-center'
                                style={{ animationDelay: '3.75s' }}
                            >
                                $1000
                            </span>
                        </div>
                    </div>

                    {/* Midnight Countdown Box (Isolated Component) */}
                    <BonusCountdownTimer />

                    {/* Step 1: Initial CTA Button */}
                    {step === 'cta' && (
                        <div className='flex flex-col gap-3'>
                            <button
                                type='button'
                                onClick={handleCtaClick}
                                className='w-full py-3.5 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold text-xs shadow-md shadow-blue-500/25 transition-all duration-200 cursor-pointer flex items-center justify-center gap-2'
                            >
                                <Gift className='w-5 h-5' />
                                <span>آفر و {offerTypeWord} را برایم ارسال کن</span>
                            </button>
                            <span className='text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed'>
                                ارسال رایگان لینک فعال‌سازی و جزییات دریافت {offerTypeWord}
                            </span>
                        </div>
                    )}

                    {/* Step 2A: Logged-in User Confirmation Step */}
                    {step === 'logged_in_confirm' && (
                        <div className='w-full flex flex-col gap-3.5 text-right'>
                            <div className='flex items-center justify-between border-b border-gray-200/80 dark:border-white/10 pb-2'>
                                <span className='text-xs font-bold text-gray-800 dark:text-gray-200'>
                                    تأیید شماره جهت دریافت {offerTypeWord}
                                </span>
                                <button
                                    type='button'
                                    onClick={() => setStep('cta')}
                                    className='text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer'
                                >
                                    بازگشت
                                </button>
                            </div>

                            {/* User Phone Confirmation Box */}
                            <div className='flex flex-row-reverse items-center gap-3 p-2 rounded-xl bg-white dark:bg-white/5 border border-blue-100 dark:border-white/10 shadow-2xs'>
                                <div className='size-8 rounded-full bg-blue-600/10 dark:bg-blue-400/15 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0'>
                                    <Phone className='w-5 h-5' />
                                </div>
                                <div className='flex flex-col'>
                                    <span
                                        dir='ltr'
                                        className='text-sm font-bold font-mono text-gray-900 dark:text-white'
                                    >
                                        {activeMobile}
                                    </span>
                                </div>
                            </div>

                            <p className='text-xxs text-gray-600 dark:text-gray-300 leading-relaxed'>
                                آفر و {offerTypeWord} برای شماره فوق ثبت می‌شود. آیا تأیید می‌کنید؟
                            </p>

                            <div className='flex flex-col gap-2 pt-1'>
                                <button
                                    type='button'
                                    disabled={loading}
                                    onClick={handleLoggedInRegister}
                                    className='w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 active:scale-[0.98] text-white font-bold text-sm sm:text-base shadow-md shadow-blue-500/25 transition-all cursor-pointer flex items-center justify-center gap-2'
                                >
                                    {loading ? (
                                        <div className='flex items-center gap-2'>
                                            <LoaderSpin />
                                            <span className='text-sm'>در حال ثبت شماره...</span>
                                        </div>
                                    ) : (
                                        <span>تأیید و دریافت {offerTypeWord}</span>
                                    )}
                                </button>

                                <button
                                    type='button'
                                    disabled={loading}
                                    onClick={() => setStep('login_form')}
                                    className='w-full py-2.5 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer text-center'
                                >
                                    استفاده از شماره دیگر
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2B: Update Name & Email for logged-in user after phone registration */}
                    {step === 'update_profile' && (
                        <form
                            onSubmit={handleProfileUpdateSubmit}
                            className='w-full flex flex-col gap-3 text-right'
                        >
                            <div className='flex items-center justify-between border-b border-gray-200/80 dark:border-white/10 pb-2'>
                                <span className='text-xs font-bold text-gray-800 dark:text-gray-200'>
                                    تکمیل مشخصات
                                </span>
                                <span
                                    dir='ltr'
                                    className='text-xs font-mono text-gray-500 dark:text-gray-400'
                                >
                                    {activeMobile}
                                </span>
                            </div>

                            <p className='text-xs text-gray-600 dark:text-gray-300 leading-relaxed'>
                                جهت هماهنگی کارشناسان برای دریافت {offerTypeWord}، لطفاً نام و ایمیل
                                خود را بررسی نمایید:
                            </p>

                            <div className='relative'>
                                <UserIcon className='w-4 h-4 absolute right-3.5 top-3.5 text-gray-400 pointer-events-none' />
                                <input
                                    type='text'
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder='نام و نام خانوادگی'
                                    required
                                    disabled={loading}
                                    className='w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all'
                                />
                            </div>

                            <div className='relative'>
                                <Mail className='w-4 h-4 absolute right-3.5 top-3.5 text-gray-400 pointer-events-none' />
                                <input
                                    type='email'
                                    dir='ltr'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder='ایمیل (اختیاری)'
                                    disabled={loading}
                                    className='w-full pr-10 pl-4 py-2.5 rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-black/20 text-gray-900 dark:text-white text-sm font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all'
                                />
                            </div>

                            <div className='flex flex-col gap-2 pt-1'>
                                <button
                                    type='submit'
                                    disabled={loading || !fullName.trim()}
                                    className='w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 active:scale-[0.98] text-white font-bold text-sm sm:text-base shadow-md shadow-blue-500/25 transition-all cursor-pointer flex items-center justify-center gap-2'
                                >
                                    {loading ? (
                                        <div className='flex items-center gap-2'>
                                            <LoaderSpin />
                                            <span className='text-sm'>در حال ثبت اطلاعات...</span>
                                        </div>
                                    ) : (
                                        <span>ثبت نهایی و دریافت {offerTypeWord}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Step 2C: Login / Register Wizard for guests or different number */}
                    {step === 'login_form' && (
                        <div className='w-full flex flex-col'>
                            <div className='flex items-center justify-between mb-3 border-b border-gray-200/80 dark:border-white/10 pb-2'>
                                <span className='text-xs font-bold text-gray-700 dark:text-gray-200'>
                                    دریافت {offerTypeWord} و آفرهای ویژه
                                </span>
                                <button
                                    type='button'
                                    onClick={() => setStep('cta')}
                                    className='text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer'
                                >
                                    بازگشت
                                </button>
                            </div>

                            <LoginFormComponent
                                landingSlug='bonus'
                                httpReferrer={referrerUrl || getReferrerUrl()}
                                popupWizardStyle={true}
                                newStyle={true}
                                textBtn={'دریافت ' + offerTypeWord}
                                otpBtnText='تأیید کد'
                                handleLoginSuccess={async () => {
                                    const currentToken = getAuthToken();
                                    const currentSourceUrl = getReferrerUrl() || referrerUrl;
                                    const phone = normalizeToEnglishDigits(
                                        activeMobile ||
                                            (typeof window !== 'undefined'
                                                ? localStorage.getItem('user_phone') || ''
                                                : ''),
                                    );

                                    if (currentToken && phone) {
                                        try {
                                            await notifyLandingRegister(
                                                phone,
                                                currentToken,
                                                'bonus',
                                                currentSourceUrl,
                                            );
                                        } catch (err) {
                                            console.warn(
                                                'Fallback notifyLandingRegister error:',
                                                err,
                                            );
                                        }
                                    }

                                    setStep('done');
                                    toast({
                                        className: 'bg-emerald-600 text-white rounded-2xl',
                                        title: `ثبت‌نام دریافت ${offerTypeWord} با موفقیت انجام شد!`,
                                        duration: 4000,
                                    });
                                }}
                            />
                        </div>
                    )}

                    {/* Step 3: Success / Done State */}
                    {step === 'done' && (
                        <div className='flex flex-col gap-3.5 p-4 rounded-2xl bg-white dark:bg-white/5 border border-emerald-500/30 text-right'>
                            <div className='flex items-center gap-3'>
                                <div className='size-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm'>
                                    <Check className='size-4 stroke-[3]' />
                                </div>
                                <div className='font-bold text-sm text-gray-900 dark:text-white'>
                                    ثبت‌نام شما با موفقیت انجام شد
                                </div>
                            </div>

                            <div className='flex items-start gap-2.5 text-xs text-gray-600 dark:text-gray-300 leading-relaxed pt-2 border-t border-gray-100 dark:border-white/5'>
                                <MessageSquare className='w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5' />
                                <span>
                                    لینک و شرایط دریافت آفرها و {offerTypeWord} به زودی برای شما
                                    پیامک خواهد شد.
                                </span>
                            </div>

                            <div className='flex items-start gap-2.5 text-xs text-gray-700 dark:text-gray-200 font-medium leading-relaxed bg-blue-50/60 dark:bg-white/5 p-2.5 rounded-xl border border-blue-100/60 dark:border-white/5'>
                                <PhoneCall className='w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5' />
                                <span>
                                    {' '}
                                    همچنین درصورت نیاز کارشناسان ما به زودی با شما تماس خواهند گرفت؛
                                    لطفاً تلفن خود را در دسترس نگه دارید.
                                </span>
                            </div>

                            <button
                                type='button'
                                onClick={() => setStep('cta')}
                                className='text-xs text-blue-600 dark:text-blue-400 hover:underline text-center mt-2 cursor-pointer'
                            >
                                ثبت شماره دیگر
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Embedded CSS Keyframes for Gift Dance & Amount Cycling */}
            <style jsx>{`
                .gift-dance-box {
                    perspective: 400px;
                }
                .gift-dance-box span {
                    display: inline-block;
                    transform-style: preserve-3d;
                    animation: giftDance 5s cubic-bezier(0.34, 1.4, 0.5, 1) infinite;
                }
                @keyframes giftDance {
                    0% {
                        transform: rotate(0deg) scale(1) translateY(0);
                    }
                    8% {
                        transform: rotate(-10deg) scale(1.08) translateY(-3px);
                    }
                    16% {
                        transform: rotate(10deg) scale(1.08) translateY(-3px);
                    }
                    24% {
                        transform: rotate(-6deg) scale(1.04) translateY(0);
                    }
                    30% {
                        transform: rotate(0deg) scale(1) translateY(0);
                    }
                    38% {
                        transform: rotate(0deg) scale(1.12) translateY(-8px);
                    }
                    44% {
                        transform: rotate(0deg) scale(0.96) translateY(0);
                    }
                    50% {
                        transform: rotate(0deg) scale(1) translateY(0);
                    }
                    62% {
                        transform: rotateY(180deg) scale(1.06);
                    }
                    74% {
                        transform: rotateY(360deg) scale(1);
                    }
                    100% {
                        transform: rotateY(360deg) scale(1);
                    }
                }
                .amt-cycle {
                    opacity: 0;
                    animation: amtCycle 5s linear infinite;
                }
                @keyframes amtCycle {
                    0%,
                    1% {
                        opacity: 0;
                        transform: translateY(8px) scale(0.92);
                    }
                    7%,
                    20% {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                    26%,
                    100% {
                        opacity: 0;
                        transform: translateY(-8px) scale(0.92);
                    }
                }
            `}</style>
        </section>
    );
};

export default PlatformBonusCard;
